import { useAppDisplayModeContext } from '@archipro-design/aria';
import { MainInfoRightSectionItem } from './MainInfoRightSectionItem';

import type { OverviewLoaderData } from '@modules/professional/page/OverviewPage';
import { useLoaderData } from '@remix-run/react';
import InfoSectionMobile from '~/modules/detail/components/info-section/InfoSectionMobile';
import {
    formatNumberWithCommas,
    formatTimeSpent,
    shortenCount,
} from '@rocco/utils/format-number';
import { EyeIcon, BookmarkIcon, BookOpenIcon } from 'lucide-react';
import { useState } from 'react';
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from '@rocco/components/popover';

export const PublicKPI: React.FC = () => {
    const { professionalOverview } = useLoaderData<OverviewLoaderData>();
    const { mobile } = useAppDisplayModeContext();
    const publicKPI = professionalOverview?.AggregatedPublicKPI;
    const [openPopover, setOpenPopover] = useState<string | null>(null);
    if (!publicKPI) {
        return null;
    }

    const { TotalPageViews, TotalSaves, TotalImpressions, TotalTimeSpent } =
        publicKPI;
    const totalViews = (TotalPageViews ?? 0) + (TotalImpressions ?? 0);
    const formattedTotalViews =
        totalViews > 100 ? shortenCount(totalViews ?? 0) : null;

    const totalSaves = TotalSaves ?? 0;
    const formattedTotalSaves =
        totalSaves > 100 ? shortenCount(totalSaves ?? 0) : null;

    const totalTimeSpent = TotalTimeSpent ?? 0;
    const formattedTotalTimeSpent =
        totalTimeSpent > 100 ? formatTimeSpent(totalTimeSpent ?? 0) : null;

    const availableMetrics = [
        {
            name: 'Total Views',
            value: formattedTotalViews,
            icon: <EyeIcon className="size-5" />,
            shouldShow: formattedTotalViews !== null,
            title: `${formatNumberWithCommas(totalViews)} profile impressions.`,
        },
        {
            name: 'Total Saves',
            value: formattedTotalSaves,
            icon: <BookmarkIcon className="size-5" />,
            shouldShow: formattedTotalSaves !== null,
            title: `${professionalOverview?.Title} has been saved ${formatNumberWithCommas(totalSaves)} times.`,
        },
        {
            name: 'Total Time Spent',
            value: formattedTotalTimeSpent,
            icon: <BookOpenIcon className="size-5" />,
            shouldShow: formattedTotalTimeSpent !== null,
            title: `Together, ArchiPro users spent ${Math.floor(totalTimeSpent / 3600)} hours reading this profile.`,
        },
    ];

    return mobile ? (
        <InfoSectionMobile label="Views and Engagement">
            <div className="flex flex-col items-end gap-1">
                {availableMetrics.map((metric) => {
                    if (!metric.shouldShow) return null;
                    return (
                        <Popover
                            key={metric.name}
                            open={openPopover === metric.name}
                            onOpenChange={(open) =>
                                setOpenPopover(open ? metric.name : null)
                            }
                        >
                            <PopoverTrigger asChild>
                                <div
                                    className="flex cursor-pointer flex-row items-center gap-1"
                                    onMouseEnter={() =>
                                        setOpenPopover(metric.name)
                                    }
                                    onMouseLeave={() => setOpenPopover(null)}
                                >
                                    {metric.icon}
                                    {metric.value}
                                </div>
                            </PopoverTrigger>
                            <PopoverContent
                                side="top"
                                className="max-w-xs border border-gray-200 bg-gray-200 p-2.5 text-sm shadow-lg"
                            >
                                {metric.title}
                            </PopoverContent>
                        </Popover>
                    );
                })}
            </div>
        </InfoSectionMobile>
    ) : (
        <MainInfoRightSectionItem label="Views and Engagement">
            <div className="flex flex-row gap-6">
                {availableMetrics.map((metric) => {
                    if (!metric.shouldShow) return null;
                    return (
                        <Popover
                            key={metric.name}
                            open={openPopover === metric.name}
                            onOpenChange={(open) =>
                                setOpenPopover(open ? metric.name : null)
                            }
                        >
                            <PopoverTrigger asChild>
                                <div
                                    className="flex cursor-pointer flex-row items-center gap-1"
                                    onMouseEnter={() =>
                                        setOpenPopover(metric.name)
                                    }
                                    onMouseLeave={() => setOpenPopover(null)}
                                >
                                    {metric.icon}
                                    {metric.value}
                                </div>
                            </PopoverTrigger>
                            <PopoverContent
                                side="top"
                                className="max-w-xs border border-gray-200 bg-gray-200 p-2.5 text-sm shadow-lg"
                            >
                                {metric.title}
                            </PopoverContent>
                        </Popover>
                    );
                })}
            </div>
        </MainInfoRightSectionItem>
    );
};
